import { useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Orixa Onboarding Tour.
 *
 * Triggers ONCE on first visit to /dashboard for a user whose
 * `user.onboarding_completed` is not true. Persists completion via
 * POST /api/auth/onboarding. The user can also re-launch the tour from
 * Settings (via the global `window.__orixa_start_tour()` helper).
 */
export default function Tour() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const driverRef = useRef(null);

  const finish = useCallback(async () => {
    try {
      await api.post("/auth/onboarding", { completed: true });
      if (refresh) await refresh();
    } catch { /* best effort */ }
  }, [refresh]);

  const start = useCallback(() => {
    if (driverRef.current?.isActive?.()) return;
    if (location.pathname !== "/dashboard") navigate("/dashboard");

    const d = driver({
      showProgress: true,
      animate: true,
      smoothScroll: true,
      allowClose: true,
      doneBtnText: "Get started",
      nextBtnText: "Next",
      prevBtnText: "Back",
      progressText: "{{current}} / {{total}}",
      popoverClass: "orixa-tour",
      onDestroyStarted: () => {
        finish();
        d.destroy();
      },
      steps: [
        {
          element: '[data-testid="sidebar-logo"]',
          popover: {
            title: "Welcome to Orixa Intelligence",
            description:
              "Your AI-powered investment, origination and execution operating system for Emerging Markets. This 60-second tour will show you the essentials.",
            side: "right",
            align: "start",
          },
        },
        {
          element: '[data-testid="sidebar-new-assessment"]',
          popover: {
            title: "Launch a new mandate",
            description:
              "Every investment thesis starts here. Define your target market, sector, capital envelope, and Orixa will orchestrate the full intelligence pipeline.",
            side: "right",
          },
        },
        {
          element: '[data-testid="nav-workspaces"]',
          popover: {
            title: "Workspaces — your deal rooms",
            description:
              "Each mandate becomes a workspace. Inside, you launch one of six institutional workflows: Launch, Evaluate, Holding, Raise, Expand or Structure.",
            side: "right",
          },
        },
        {
          element: '[data-testid="nav-radar"]',
          popover: {
            title: "Orixa Radar",
            description:
              "Live AI scanning of emerging sectors, ranked markets and named hotspots across the continent. Every signal links to a one-click workflow.",
            side: "right",
          },
        },
        {
          element: '[data-testid="ai-analyst-launcher"]',
          popover: {
            title: "Your AI Analyst",
            description:
              "Always-on copilot — ask anything about your workspaces, deliverables or strategy. Workspace-aware, citation-grounded, actionable.",
            side: "left",
          },
        },
      ],
    });
    driverRef.current = d;
    d.drive();
  }, [location.pathname, navigate, finish]);

  useEffect(() => {
    window.__orixa_start_tour = () => start();
    return () => { delete window.__orixa_start_tour; };
  }, [start]);

  useEffect(() => {
    if (!user) return;
    if (location.pathname !== "/dashboard") return;
    if (user.onboarding_completed) return;
    const t = setTimeout(() => start(), 700);
    return () => clearTimeout(t);
  }, [user, location.pathname, start]);

  return null;
}
